import ua from '@tencent/docs-user-agent';
import { FeatureMessageCenter, type IFeatureMessageCenter } from '@tencent/docs-connection';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { BUTTON_ACTION_TYPE, IInactivityGuide, IInactivityGuideServices } from '@src/slide/application/service/inactivity-guide/core/interface';
import { onSlideAppReady } from '@src/slide/domain/slide-app/core/slide-data-services.core';

export enum FeatureMessageType {
    Notification = 1,
    PushMessage = 2,
}

/**
 * 非编辑消息通道的通用消息结构
 */
export interface IFeatureMessage {
    msgType: FeatureMessageType;
}

/**
 * 推送消息（msgType=2）的消息结构
 */
export interface IPushMessage extends IFeatureMessage {
    msgType: FeatureMessageType.PushMessage;
    content?: string;
    btnText?: string;
    duration?: number;
    btnAction?: string; // 跳转链接，配置了什么链接前端就打开什么链接
    btnActionType: string;
    reportOnAppear?: { [key: string]: string };
    reportOnClick?: { [key: string]: string };
}

/**
 * 非编辑消息通道（/socket.io/）的推送消息通知处理逻辑
 * 负责响应后台推送的消息并展示在详情页（PC、移动）
 * @story https://tapd.woa.com/TIM_web/prong/stories/view/1010143861116313521
 */
export default abstract class InactivityGuideService implements IInactivityGuide {
    private featureMessageCenter?: IFeatureMessageCenter;

    protected abstract handleCollect;

    public constructor(protected services: IInactivityGuideServices) {
        this.featureMessageCenter = FeatureMessageCenter.getInstance();
        this.featureMessageCenter.onFeatureMessageEvent(this.onMessage);
        onSlideAppReady(() => {
            this.postReadyMessage();
        });
    }

    public setServicesDelegate(services: IInactivityGuideServices): void {
        this.services = services;
    }

    public dispose() {
        this.featureMessageCenter?.dispose();
        this.featureMessageCenter = undefined;
    }

    protected handleToPdf = () => {};

    protected handleDownload = () => {};

    protected handleBtnActionType = async (actionType: string) => {
        switch (actionType) {
            case BUTTON_ACTION_TYPE.CLONE: {
                this.services.produceBackupManager.promise().then((produceBackupManager) => {
                    produceBackupManager.generateProduceBackup();
                });
                break;
            }
            case BUTTON_ACTION_TYPE.TO_PDF: {
                this.handleToPdf();
                break;
            }
            case BUTTON_ACTION_TYPE.DOWNLOAD: {
                this.handleDownload();
                break;
            }
            case BUTTON_ACTION_TYPE.COLLECT: {
                this.handleCollect();
                break;
            }
        }
        return;
    };

    private postReadyMessage = () => {
        if (!window.clientVars) {
            return;
        }
        const { userId, padId } = window.clientVars;
        FeatureMessageCenter.getInstance().sendMessage({
            type: 'RETENTION_MESSAGE',
            uid: userId,
            padid: padId,
            docs_type: 'slide',
            isMiniProgram: true, // 给后端判断是否是wx小程序环境，只有wx小程序推送下载文案
        });
    };

    private onMessage = (message: any) => {
        const data: IPushMessage = JSON.parse(JSON.parse(message.data)?.message);

        if (data.msgType !== FeatureMessageType.PushMessage) {
            return;
        }
        const { content, btnAction, btnText, duration, reportOnAppear, btnActionType, reportOnClick } = data;
        if (!content) {
            return;
        }

        this.showSnackbar(content, btnActionType, btnAction, btnText, duration, reportOnClick);

        if (reportOnAppear) {
            tdw({
                ...reportOnAppear,
                ver5: content,
                ver6: btnActionType,
            });
        }
    };

    protected abstract showSnackbar(
        content: string,
        btnActionType: string,
        btnAction?: string,
        btnText?: string,
        duration?: number,
        reportOnClick?: {
            [key: string]: string;
        }
    );
}
